import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Table, message } from "antd";
import { fetchFaxLog } from "../../../service/fax";
import {
  LogViewWrapper,
  LogToolbar,
  LogSearchInput,
  LogTableWrapper,
  FaxStatusPill,
  MetaText,
  PreviewFrame,
  PrimaryBtn,
} from "../FaxStyled";

const LOG_PAGE_SIZE = 20;

const DISPOSITION_LABELS = {
  in_sorting_queue: "In sorting queue",
  assigned: "Assigned",
  deleted: "Deleted",
};

/**
 * Fax Log tab: page through everything that arrived on the firm's fax
 * lines, regardless of where it is in the mail workflow now (still in
 * the sorting queue, already assigned to a matter, or deleted as
 * junk). Previews open in a modal from the row's short-lived
 * presigned URL — if the URL expired while the page sat open, a
 * refresh re-mints it.
 *
 * With `line` (an E.164 fax number) the log is limited to faxes dialed
 * to that one registered line — used by the Intake tab.
 */
const FaxLogView = ({ line = "" }) => {
  const [data, setData] = useState({ results: [], count: 0 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  // Starts true: the mount fetch fires immediately below, and the
  // table must show its loading state rather than flashing antd's
  // "No data" before the first request has even started.
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { url, title }

  // Monotonic request sequence: a slow older response must never
  // overwrite a newer one (fast typing can have several in flight).
  const loadSeqRef = useRef(0);
  // Pending search-debounce timer, held in a ref so pagination can
  // cancel it — a page click during the debounce window must not be
  // overwritten by a late page-1 search fetch for the same query.
  const debounceRef = useRef(null);
  const isFirstSearchEffect = useRef(true);

  const load = useCallback(async (targetPage = 1, targetSearch = "") => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const response = await fetchFaxLog({
        page: targetPage,
        pageSize: LOG_PAGE_SIZE,
        search: targetSearch,
        line,
      });
      if (seq !== loadSeqRef.current) return; // stale response
      setData(response.data);
      setPage(targetPage);
    } catch {
      if (seq === loadSeqRef.current) {
        message.error("Could not load the fax log.");
      }
    } finally {
      if (seq === loadSeqRef.current) {
        setLoading(false);
      }
    }
  }, [line]);

  // Mount fetch fires immediately — only SUBSEQUENT search changes
  // debounce (the skip-first-run ref below).
  useEffect(() => {
    load(1, "");
  }, [load]);

  useEffect(() => {
    if (isFirstSearchEffect.current) {
      isFirstSearchEffect.current = false;
      return undefined;
    }
    // Invalidate any in-flight request IMMEDIATELY on search change,
    // not just when the debounced load fires — otherwise a slow
    // response for the previous query could render during the 350ms
    // debounce window while the input already shows the new text.
    loadSeqRef.current += 1;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(1, search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search, load]);

  const lineColumn = {
    title: "Line",
    dataIndex: "fax_line_label",
    key: "fax_line_label",
    width: 140,
    render: (value, row) =>
      value || <MetaText>{row.to_number || "—"}</MetaText>,
  };

  const columns = [
    {
      title: "Received",
      dataIndex: "received_at",
      key: "received_at",
      width: 180,
      render: (value) => new Date(value).toLocaleString(),
    },
    { title: "From", dataIndex: "from_number", key: "from_number", width: 140 },
    // A single-line view makes the Line column redundant.
    ...(line ? [] : [lineColumn]),
    {
      title: "Pages",
      dataIndex: "num_pages",
      key: "num_pages",
      width: 70,
      render: (value) => value ?? <MetaText>—</MetaText>,
    },
    {
      title: "Document",
      dataIndex: "document_name",
      key: "document_name",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "disposition",
      key: "disposition",
      width: 150,
      render: (value, row) => (
        <span>
          <FaxStatusPill tone={value}>
            {DISPOSITION_LABELS[value] || value}
          </FaxStatusPill>
          {row.assigned_matter_name && (
            <div>
              <MetaText>{row.assigned_matter_name}</MetaText>
            </div>
          )}
        </span>
      ),
    },
    {
      title: "",
      key: "preview",
      width: 100,
      render: (_, row) =>
        row.preview_url ? (
          <PrimaryBtn
            style={{ padding: "5px 12px", fontSize: 12 }}
            onClick={() =>
              setPreview({
                url: row.preview_url,
                title: row.document_name || "Fax preview",
              })
            }
          >
            Preview
          </PrimaryBtn>
        ) : (
          <MetaText>—</MetaText>
        ),
    },
  ];

  return (
    <LogViewWrapper>
      <LogToolbar>
        <LogSearchInput
          placeholder="Search by name or fax number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="fax-log-search"
        />
      </LogToolbar>

      <LogTableWrapper>
        <Table
          size="middle"
          rowKey="id"
          columns={columns}
          dataSource={data.results}
          loading={loading}
          pagination={{
            current: page,
            pageSize: LOG_PAGE_SIZE,
            total: data.count,
            showSizeChanger: false,
            onChange: (nextPage) => {
              // A page click cancels any pending search debounce so a
              // late page-1 fetch can't override the chosen page.
              clearTimeout(debounceRef.current);
              load(nextPage, search);
            },
          }}
        />
      </LogTableWrapper>

      <Modal
        open={Boolean(preview)}
        title={preview?.title}
        footer={null}
        width="80vw"
        onCancel={() => setPreview(null)}
        destroyOnClose
      >
        {preview && (
          <PreviewFrame
            src={preview.url}
            title={preview.title}
            data-testid="fax-preview-frame"
          />
        )}
      </Modal>
    </LogViewWrapper>
  );
};

FaxLogView.propTypes = {
  line: PropTypes.string,
};

export default FaxLogView;
